import { Platform } from 'react-native';

interface AudioRecorderConfig {
  onDataAvailable: (data: Blob) => void;
  onSilenceDetected: () => void;
  silenceThreshold?: number; // in decibels
  silenceDuration?: number; // in milliseconds
}

class WebRTCService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private config: AudioRecorderConfig | null = null;
  private stream: MediaStream | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      // Removed initialization of audioContext here
    }
  }

  async initialize(config: AudioRecorderConfig) {
    if (Platform.OS !== 'web') {
      throw new Error('WebRTC is only supported on web platform');
    }

    this.config = {
      silenceThreshold: -50, // default silence threshold in dB
      silenceDuration: 1000, // default 1 second of silence
      ...config,
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Create AudioContext only when needed
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        await this.audioContext.resume(); // Ensure it's resumed after user interaction
      }

      // Set up audio analysis
      if (this.audioContext && this.stream) {
        const source = this.audioContext.createMediaStreamSource(this.stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        source.connect(this.analyser);
      }

      return true;
    } catch (error) {
      console.error('Error initializing WebRTC:', error);
      throw error;
    }
  }

  startRecording() {
    if (!this.stream || !this.config) return;

    this.mediaRecorder = new MediaRecorder(this.stream);
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.config?.onDataAvailable(event.data);
      }
    };

    this.mediaRecorder.start();
    this.startSilenceDetection();
  }

  private startSilenceDetection() {
    if (!this.analyser || !this.config) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    let silenceStart: number | null = null;

    const checkSilence = () => {
      if (!this.analyser || !this.config) return;

      this.analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
      const decibels = 20 * Math.log10(average / 255);

      if (decibels < (this.config.silenceThreshold ?? -50)) {
        if (!silenceStart) {
          silenceStart = Date.now();
        } else if (Date.now() - silenceStart >= (this.config.silenceDuration ?? 1000)) {
          this.config.onSilenceDetected();
          return;
        }
      } else {
        silenceStart = null;
      }

      requestAnimationFrame(checkSilence);
    };

    requestAnimationFrame(checkSilence);
  }

  getAudioLevel(): number {
    if (!this.analyser) return 0;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteFrequencyData(dataArray);
    return dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
  }

  getAudioStream(): MediaStream | null {
    return this.stream;
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  cleanup() {
    this.stopRecording();
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.analyser = null;
    this.mediaRecorder = null;
    this.config = null;
  }
}

export const webRTCService = new WebRTCService();
